import React, { useState, useEffect } from 'react';
import { X, ArrowRight, BookOpen, Target, Clock, AlertCircle, Zap, CheckCircle, Play, Lightbulb } from 'lucide-react';
import { Books, TrendUp, Plus, Lightning, Path, Compass } from '@phosphor-icons/react';
import { useAuthStore } from '../stores/authStore';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface DiscoverModalProps {
  isOpen: boolean;
  onClose: () => void;
  context: 'library' | 'learning-paths' | 'learning-path-detail';
  contextData?: {
    pathId?: string;
    itemId?: string;
  };
}

interface LibraryInsights {
  pathSuggestions: Array<{
    topic: string;
    itemCount: number;
    estimatedHours: number;
    type: string;
    items: Array<{ id: string; title: string }>;
  }>;
  contentClusters: Array<{
    tag: string;
    count: number;
    items: Array<{ id: string; title: string }>;
  }>;
  contentGaps: Array<{
    category: string;
    currentCount: number;
    neededForPath: number;
    suggestion: string;
  }>;
  readingOptimization: {
    totalItems: number;
    totalHours: number;
    shortReads: number;
    longReads: number;
    averageReadingTime: number;
    quickWins: Array<{ id: string; title: string; minutes: number }>;
  };
  totalContent: number;
}

interface PathsInsights {
  inProgressPaths: Array<{
    id: string;
    title: string;
    topic: string;
    progress: number;
    completedItems: number;
    totalItems: number;
    estimatedRemainingHours: number;
  }>;
  abandonedPaths: Array<{
    id: string;
    title: string;
    topic: string;
    progress: number;
    completedItems: number;
    totalItems: number;
    daysInactive: number;
    estimatedRemainingHours: number;
  }>;
  notStartedPaths: Array<{
    id: string;
    title: string;
    topic: string;
    estimatedHours: number;
    itemCount: number;
  }>;
  completedPaths: Array<{
    id: string;
    title: string;
    topic: string;
  }>;
  timeInvestment: {
    totalEstimatedHours: number;
    completedHours: number;
    remainingHours: number;
  };
  stats: {
    totalPaths: number;
    activePaths: number;
    completedCount: number;
    inProgressCount: number;
  };
}

interface DetailInsights {
  relatedContent: Array<{
    id: string;
    title: string;
    category: string;
    readingTime: number;
    reason: string;
  }>;
  supplementaryMaterials: Array<{
    id: string;
    title: string;
    difficulty: string;
    readingTime: number;
    reason: string;
  }>;
  prerequisites: Array<{
    id?: string;
    title: string;
    difficulty: string;
    reason: string;
    source?: 'content' | 'ai';
  }>;
  nextSteps: Array<{
    id: string;
    title: string;
    difficulty: string;
    reason: string;
  }>;
  externalResources: Array<{
    domain: string;
    suggestion: string;
  }>;
  pathProgress: number;
  currentItemTitle: string;
}

export default function DiscoverModal({ isOpen, onClose, context, contextData }: DiscoverModalProps) {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [libraryInsights, setLibraryInsights] = useState<LibraryInsights | null>(null);
  const [pathsInsights, setPathsInsights] = useState<PathsInsights | null>(null);
  const [detailInsights, setDetailInsights] = useState<DetailInsights | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'clusters' | 'gaps' | 'time'>('clusters');

  useEffect(() => {
    if (isOpen) {
      loadInsights();
    }
  }, [isOpen, context, contextData?.pathId, contextData?.itemId]);

  const loadInsights = async () => {
    if (!token) return;

    setIsLoading(true);
    try {
      if (context === 'library') {
        const response = await fetch('/api/learning-paths/library-insights', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setLibraryInsights(data.data);
        }
      } else if (context === 'learning-paths') {
        const response = await fetch('/api/learning-paths/paths-insights', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setPathsInsights(data.data);
        }
      } else if (context === 'learning-path-detail') {
        // Fetch insights for current learning path and item
        if (contextData?.pathId) {
          const url = `/api/learning-paths/${contextData.pathId}/detail-insights${contextData.itemId ? `?itemId=${contextData.itemId}` : ''}`;
          const response = await fetch(url, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setDetailInsights(data.data);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load insights:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePath = (topic: string) => {
    onClose();
    navigate(`/learning-paths?discover=true&topic=${encodeURIComponent(topic)}`);
  };

  const handleGoToPath = (pathId: string) => {
    onClose();
    navigate(`/learning-paths/${pathId}`);
  };

  const getContextTitle = () => {
    switch (context) {
      case 'library':
        return 'Library Insights';
      case 'learning-paths':
        return 'Learning Recommendations';
      case 'learning-path-detail':
        return 'Contextual Learning';
      default:
        return 'Discover';
    }
  };

  const getContextSubtitle = () => {
    switch (context) {
      case 'library':
        return 'Organize your content into structured trails';
      case 'learning-paths':
        return 'Personalized recommendations to maximize your learning';
      case 'learning-path-detail':
        return 'Expand your knowledge with related materials';
      default:
        return '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div 
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700">
              <Compass weight="duotone" className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {getContextTitle()}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {getContextSubtitle()}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="text-center py-16">
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="w-3 h-3 rounded-full bg-gray-400 animate-[bounce_1s_ease-in-out_infinite]" />
                <span className="w-3 h-3 rounded-full bg-gray-500 animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.15s' }} />
                <span className="w-3 h-3 rounded-full bg-gray-600 animate-[bounce_1s_ease-in-out_infinite]" style={{ animationDelay: '0.3s' }} />
              </div>
              <p className="text-gray-500 dark:text-gray-400">Analyzing your content...</p>
            </div>
          ) : context === 'library' ? (
            <LibraryContent 
              insights={libraryInsights} 
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              onCreatePath={handleCreatePath}
              onViewContent={(contentId: string) => {
                onClose();
                navigate(`/library?content=${contentId}`);
              }}
            />
          ) : context === 'learning-paths' ? (
            <PathsContent 
              insights={pathsInsights}
              onGoToPath={handleGoToPath}
            />
          ) : context === 'learning-path-detail' ? (
            <DetailContent 
              insights={detailInsights}
              onClose={onClose}
              contextData={contextData}
            />
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">No suggestions available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 
                     rounded-xl font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ LIBRARY PAGE CONTENT ============
function LibraryContent({ 
  insights, 
  activeTab, 
  setActiveTab, 
  onCreatePath,
  onViewContent
}: { 
  insights: LibraryInsights | null; 
  activeTab: string;
  setActiveTab: (tab: 'clusters' | 'gaps' | 'time') => void;
  onCreatePath: (topic: string) => void;
  onViewContent: (contentId: string) => void;
}) {
  if (!insights) {
    return (
      <div className="text-center py-12 px-6">
        <Books className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400 mb-2">No content to analyze yet</p>
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Save some articles to unlock personalized insights
        </p>
      </div>
    );
  }

  const tabs = [
    { id: 'clusters', label: 'Topic Clusters', icon: Target, count: insights.contentClusters.length, color: 'text-orange-500 dark:text-orange-400' },
    { id: 'gaps', label: 'Content Gaps', icon: AlertCircle, count: insights.contentGaps.length, color: 'text-amber-500 dark:text-amber-400' },
    { id: 'time', label: 'Quick Reads', icon: Zap, count: insights.readingOptimization.quickWins.length, color: 'text-emerald-500 dark:text-emerald-400' },
  ];

  return (
    <div className="p-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-3 text-center border border-gray-200 dark:border-gray-700">
          <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{insights.totalContent}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Total Items</p>
        </div>
        <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-3 text-center border border-gray-200 dark:border-gray-700">
          <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{insights.pathSuggestions.length}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Path Ideas</p>
        </div>
        <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-3 text-center border border-gray-200 dark:border-gray-700">
          <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{insights.readingOptimization.totalHours}h</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Total Reading</p>
        </div>
        <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-3 text-center border border-gray-200 dark:border-gray-700">
          <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{insights.readingOptimization.shortReads}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Quick Reads</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <tab.icon className={`w-4 h-4 ${tab.color}`} />
            {tab.label}
            {tab.count > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                activeTab === tab.id 
                  ? 'bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="space-y-3">
        {activeTab === 'clusters' && (
          insights.contentClusters.length === 0 ? (
            <EmptyState 
              icon={Target}
              title="No topic clusters found"
              description="Add more tagged content to discover related clusters"
            />
          ) : (
            insights.contentClusters.map((cluster, idx) => (
              <div key={idx} className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-medium">
                    #{cluster.tag}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {cluster.count} related items
                  </span>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">
                  {cluster.items.map(i => i.title).join(' • ')}
                </div>
              </div>
            ))
          )
        )}

        {activeTab === 'gaps' && (
          insights.contentGaps.length === 0 ? (
            <EmptyState 
              icon={AlertCircle}
              title="No content gaps"
              description="Your library is well-balanced! All topics have enough content."
            />
          ) : (
            insights.contentGaps.map((gap, idx) => (
              <div key={idx} className="p-4 rounded-xl border border-amber-200 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-200">
                      {gap.category} needs more content
                    </p>
                    <p className="text-sm text-amber-600 dark:text-amber-300 mt-1">
                      {gap.suggestion}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )
        )}

        {activeTab === 'time' && (
          insights.readingOptimization.quickWins.length === 0 ? (
            <EmptyState 
              icon={Zap}
              title="No quick reads available"
              description="Save some shorter articles for quick learning sessions"
            />
          ) : (
            <>
              <div className="p-4 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 mb-4">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  💡 <strong>Time Tip:</strong> You have {insights.readingOptimization.shortReads} articles under 5 min. 
                  Perfect for learning during breaks!
                </p>
              </div>
              {insights.readingOptimization.quickWins.map((item, idx) => (
                <button 
                  key={idx} 
                  onClick={() => onViewContent(item.id)}
                  className="w-full p-4 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                      <Zap className="w-4 h-4 text-amber-500" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-900 dark:text-white">{item.title}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{item.minutes} min read</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </button>
              ))}
            </>
          )
        )}
      </div>
    </div>
  );
}

// ============ LEARNING PATHS PAGE CONTENT ============
function PathsContent({ 
  insights, 
  onGoToPath 
}: { 
  insights: PathsInsights | null;
  onGoToPath: (pathId: string) => void;
}) {
  if (!insights) {
    return (
      <div className="text-center py-12 px-6">
        <Path className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400 mb-2">No insights available</p>
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Create some trails to unlock recommendations
        </p>
      </div>
    );
  }

  const hasContent = insights.inProgressPaths.length > 0 || 
                     insights.abandonedPaths.length > 0 ||
                     insights.notStartedPaths.length > 0;

  if (!hasContent && insights.completedPaths.length === 0) {
    return (
      <div className="text-center py-12 px-6">
        <BookOpen className="w-12 h-12 text-blue-500 mx-auto mb-4" />
        <p className="text-gray-700 dark:text-gray-300 font-medium mb-2">No trails yet</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Use the + button to create your first trail from your saved content
        </p>
      </div>
    );
  }

  if (!hasContent && insights.completedPaths.length > 0) {
    return (
      <div className="p-6">
        {/* Time Investment Summary */}
        <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <Clock className="w-5 h-5 text-blue-500" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Your Learning Stats</h3>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{insights.timeInvestment.completedHours}h</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Completed</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{insights.completedPaths.length}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Paths Done</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{insights.stats.totalPaths}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Paths</p>
            </div>
          </div>
        </div>

        <div className="text-center py-6">
          <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
          <p className="text-gray-700 dark:text-gray-300 font-medium mb-2">
            All paths completed! 🎉
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Use the + button to create a new trail
          </p>
        </div>

        {/* Show completed paths */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Your Achievements</p>
          <div className="flex flex-wrap gap-2">
            {insights.completedPaths.map(path => (
              <button
                key={path.id}
                onClick={() => onGoToPath(path.id)}
                className="px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 
                         rounded-lg text-sm hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
              >
                ✓ {path.title}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Time Investment Summary */}
      <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3 mb-3">
          <Clock className="w-5 h-5 text-blue-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white">Time Investment</h3>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{insights.timeInvestment.completedHours}h</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Completed</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{insights.timeInvestment.remainingHours}h</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Remaining</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{insights.stats.totalPaths}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Total Paths</p>
          </div>
        </div>
      </div>

      {/* In Progress Paths - Priority 1: Help users COMPLETE what they started */}
      {insights.inProgressPaths.length > 0 && (
        <Section 
          icon={<TrendUp className="w-5 h-5 text-emerald-500" />}
          title="Continue Learning"
          subtitle="Pick up where you left off"
          color="green"
        >
          {insights.inProgressPaths.map(path => (
            <SuggestionCard
              key={path.id}
              icon={<Play className="w-5 h-5 text-blue-500" />}
              title={path.title}
              description={`${path.completedItems}/${path.totalItems} items done • ~${path.estimatedRemainingHours}h left`}
              badge={`${path.progress}%`}
              badgeColor="green"
              action={() => onGoToPath(path.id)}
            />
          ))}
        </Section>
      )}

      {/* Abandoned Paths - Gentle nudge for paths not touched in 7+ days */}
      {insights.abandonedPaths.length > 0 && (
        <Section 
          icon={<Clock className="w-5 h-5 text-amber-500" />}
          title="Resume Your Journey"
          subtitle="Paths waiting for your return"
          color="amber"
        >
          {insights.abandonedPaths.map(path => (
            <SuggestionCard
              key={path.id}
              icon={<ArrowRight className="w-5 h-5 text-blue-500" />}
              title={path.title}
              description={`${path.progress}% complete • ${path.daysInactive} days since last activity`}
              badge="Resume"
              badgeColor="amber"
              action={() => onGoToPath(path.id)}
            />
          ))}
        </Section>
      )}

      {/* Not Started Paths - Only show if there's nothing in progress */}
      {insights.notStartedPaths.length > 0 && (
        <Section 
          icon={<BookOpen className="w-5 h-5 text-blue-500" />}
          title="Ready to Start"
          subtitle="Paths waiting for you"
          color="blue"
        >
          {insights.notStartedPaths.map(path => (
            <SuggestionCard
              key={path.id}
              icon={<ArrowRight className="w-5 h-5 text-blue-500" />}
              title={path.title}
              description={`${path.itemCount} items • ~${path.estimatedHours}h estimated`}
              badge="New"
              badgeColor="blue"
              action={() => onGoToPath(path.id)}
            />
          ))}
        </Section>
      )}

      {/* Completed Paths - Celebrate achievements */}
      {insights.completedPaths.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="w-5 h-5 text-emerald-500" />
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {insights.completedPaths.length} Path{insights.completedPaths.length > 1 ? 's' : ''} Completed 🎉
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {insights.completedPaths.map(path => (
              <button
                key={path.id}
                onClick={() => onGoToPath(path.id)}
                className="px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 
                         rounded-lg text-sm hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
              >
                ✓ {path.title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ HELPER COMPONENTS ============
function Section({ 
  icon, 
  title, 
  subtitle, 
  color, 
  children 
}: { 
  icon: React.ReactNode; 
  title: string; 
  subtitle: string;
  color: 'purple' | 'blue' | 'green' | 'amber';
  children: React.ReactNode;
}) {
  const colorClasses = {
    purple: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
    blue: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
    green: 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20',
    amber: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20',
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
        </div>
      </div>
      <div className="space-y-2 ml-11">
        {children}
      </div>
    </div>
  );
}

function SuggestionCard({ 
  icon, 
  title, 
  description, 
  badge, 
  badgeColor,
  action,
  addAction,
  isAISuggestion,
  isAdded
}: { 
  icon: React.ReactNode; 
  title: string; 
  description: string;
  badge?: string;
  badgeColor?: 'purple' | 'blue' | 'green' | 'amber';
  action?: () => void;
  addAction?: () => void;
  isAISuggestion?: boolean;
  isAdded?: boolean;
}) {
  const badgeColors = {
    purple: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    green: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
    amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  };

  const CardContent = (
    <>
      <div className="flex-shrink-0 p-2 rounded-lg bg-gray-100 dark:bg-gray-700 
                    text-gray-600 dark:text-gray-400 group-hover:bg-gray-200 dark:group-hover:bg-gray-600 
                    group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-gray-900 dark:text-white group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors truncate">
            {title}
          </h4>
          {badge && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badgeColors[badgeColor || 'purple']}`}>
              {badge}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
          {description}
        </p>
      </div>
      {addAction ? (
        <div
          onClick={(e) => {
            e.stopPropagation();
            if (!isAdded) addAction();
          }}
          className={`flex-shrink-0 p-3 rounded-lg transition-colors ${
            isAdded 
              ? 'bg-green-500 text-white cursor-default' 
              : 'bg-blue-500 hover:bg-blue-600 text-white cursor-pointer'
          }`}
          title={isAdded ? 'Added to trail' : 'Add to this trail'}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !isAdded) {
              e.preventDefault();
              e.stopPropagation();
              addAction();
            }
          }}
        >
          {isAdded ? <CheckCircle className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
        </div>
      ) : isAISuggestion ? (
        <div className="flex-shrink-0 p-2 rounded-lg bg-gray-100 dark:bg-gray-700" title="AI suggested topic">
          <Lightbulb className="w-5 h-5 text-amber-500" />
        </div>
      ) : (
        <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 
                            group-hover:translate-x-1 transition-all flex-shrink-0" />
      )}
    </>
  );

  if (!action) {
    return (
      <div className="w-full p-4 rounded-xl border border-gray-200 dark:border-gray-700 
                   bg-gray-50 dark:bg-gray-800/50 flex items-center gap-4">
        {CardContent}
      </div>
    );
  }

  return (
    <button
      onClick={action}
      className="w-full p-4 rounded-xl border border-gray-200 dark:border-gray-700 
               hover:border-gray-300 dark:hover:border-gray-600 
               hover:bg-gray-50 dark:hover:bg-gray-800
               transition-all text-left group flex items-center gap-4"
    >
      {CardContent}
    </button>
  );
}

// ============ KNOWLEDGE HUB DETAIL CONTENT ============
function DetailContent({ 
  insights, 
  onClose,
  contextData
}: { 
  insights: DetailInsights | null;
  onClose: () => void;
  contextData?: { pathId?: string; itemId?: string };
}) {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [addedItems, setAddedItems] = useState<Set<string>>(new Set());

  const handleViewContent = (contentId: string) => {
    onClose();
    navigate(`/library?highlight=${contentId}`);
  };

  const handleAddToPath = async (contentId: string) => {
    if (!contextData?.pathId || !token) return;

    try {
      const response = await fetch(`/api/learning-paths/${contextData.pathId}/add-items`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ contentIds: [contentId] })
      });

      if (response.ok) {
        toast.success('Added to trail!');
        setAddedItems(prev => new Set(prev).add(contentId));
      } else {
        toast.error('Failed to add item');
      }
    } catch (error) {
      console.error('Error adding item to path:', error);
      toast.error('Failed to add item');
    }
  };

  if (!insights) {
    return (
      <div className="text-center py-12 px-6">
        <Compass weight="duotone" className="w-12 h-12 text-blue-400 dark:text-blue-500 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400 mb-2">No suggestions available</p>
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Keep learning to unlock personalized recommendations
        </p>
      </div>
    );
  }

  const hasContent = insights.relatedContent.length > 0 || 
                     insights.prerequisites.length > 0 || 
                     insights.supplementaryMaterials.length > 0 ||
                     insights.nextSteps.length > 0;

  if (!hasContent) {
    return (
      <div className="text-center py-12 px-6">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <p className="text-gray-700 dark:text-gray-300 font-medium mb-2">
          You're on track! 🎯
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No additional content needed for this item. Keep going!
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Current Context */}
      {insights.currentItemTitle && (
        <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <strong>Currently viewing:</strong> {insights.currentItemTitle}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gray-500 dark:bg-gray-400 rounded-full transition-all"
                style={{ width: `${insights.pathProgress || 0}%` }}
              />
            </div>
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
              {Math.round(insights.pathProgress || 0)}%
            </span>
          </div>
        </div>
      )}

      {/* Prerequisites */}
      {insights.prerequisites.length > 0 && (
        <Section 
          icon={<BookOpen className="w-5 h-5 text-amber-500" />}
          title="Prerequisites"
          subtitle="Foundation material if you need more context"
          color="amber"
        >
          {insights.prerequisites.map((item, idx) => (
            <SuggestionCard
              key={item.id || `ai-${idx}`}
              icon={<BookOpen className="w-5 h-5 text-amber-500" />}
              title={item.title}
              description={item.reason}
              badge={item.source === 'ai' ? 'Suggested' : undefined}
              badgeColor="amber"
              action={item.source === 'ai' ? undefined : () => handleViewContent(item.id!)}
              isAISuggestion={item.source === 'ai'}
            />
          ))}
        </Section>
      )}

      {/* Related Content */}
      {insights.relatedContent.length > 0 && (
        <Section 
          icon={<Plus className="w-5 h-5 text-blue-500" />}
          title="Add to This Trail"
          subtitle="Related content that could enhance your learning"
          color="blue"
        >
          {insights.relatedContent.map((item) => (
            <SuggestionCard
              key={item.id}
              icon={<Plus className="w-5 h-5 text-blue-500" />}
              title={item.title}
              description={`${item.category} • ${item.readingTime || 10} min read`}
              badge="Related"
              badgeColor="blue"
              action={() => handleViewContent(item.id)}
              addAction={() => handleAddToPath(item.id)}
              isAdded={addedItems.has(item.id)}
            />
          ))}
        </Section>
      )}

      {/* External Resources */}
      {insights.externalResources.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            External Sources in This Trail
          </p>
          <div className="flex flex-wrap gap-2">
            {insights.externalResources.map((resource, idx) => (
              <span 
                key={idx}
                className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 
                         rounded-lg text-sm"
              >
                {resource.domain}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState({ 
  icon: Icon, 
  title, 
  description 
}: { 
  icon: React.ComponentType<any>; 
  title: string; 
  description: string; 
}) {
  return (
    <div className="text-center py-8">
      <Icon className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
      <p className="text-gray-600 dark:text-gray-400 font-medium">{title}</p>
      <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">{description}</p>
    </div>
  );
}
